import { createStore } from "./createStore";
import api from "@/utils/axios-utils";
import ASSESSMENT_TYPES, {
  ASSESSMENT_KEYS,
  makeEmptySlice,
} from "../constants/assessmentTypes";
import { ASSESSMENT_ATTEMPT_STATUS } from "@/types/assessmentAttemptStatus";

const initialAssessments = Object.fromEntries(
  ASSESSMENT_KEYS.map((k) => [k, makeEmptySlice()]),
);

const updateSlice = (assessments, type, patch) => ({
  assessments: {
    ...assessments,
    [type]: { ...assessments[type], ...patch },
  },
});

const base = (set, get) => ({
  assessments: { ...initialAssessments },

  getAssessment: (type) => get().assessments[type],

  setMeta: (type, { has, id, state, attemptsCount }) =>
    set((s) =>
      updateSlice(s.assessments, type, { has, id, state, attemptsCount }),
    ),

  fetchAssessment: async (type, lessonId, isCurrent = () => true) => {
    if (!isCurrent()) return null;
    const config = ASSESSMENT_TYPES[type];
    if (!config) return null;
    const parsedLessonId = Number(lessonId);
    if (!Number.isFinite(parsedLessonId)) return null;
    try {
      const response = await api.get(config.fetchUrl(parsedLessonId));
      if (!isCurrent()) return null;
      const data = response.data;
      if (data.has_exit_ticket) {
        set((s) =>
          updateSlice(s.assessments, type, {
            data: data.survey_json,
            id: data.id,
            has: true,
          }),
        );
        return data;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch ${config.label}:`, error);
      return null;
    }
  },

  preloadAll: async (lessonId) => {
    const { assessments } = get();
    await Promise.all(
      ASSESSMENT_KEYS.filter(
        (k) => assessments[k].has && assessments[k].id && !assessments[k].data,
      ).map((k) => get().fetchAssessment(k, lessonId)),
    );
  },

  startAttempt: async (type, isCurrent = () => true) => {
    if (!isCurrent()) return;
    const config = ASSESSMENT_TYPES[type];
    const slice = get().assessments[type];
    if (!config || !slice?.id) return;
    try {
      const response = await api.post(config.startUrl(slice.id));
      if (!isCurrent()) return;
      const { attempt_id, attempt_number } = response.data;
      set((s) =>
        updateSlice(s.assessments, type, {
          attemptId: attempt_id,
          attemptNumber: attempt_number,
          completed: false,
          score: null,
          savedResponses: null,
          percentAnswered: null,
        }),
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to start ${config.label} attempt:`, error);
      throw error;
    }
  },

  autoSave: async (type, responseJson) => {
    const config = ASSESSMENT_TYPES[type];
    const slice = get().assessments[type];
    if (!config || !slice?.attemptId) return;
    try {
      await api.post(config.autoSaveUrl(slice.id), {
        attempt_id: slice.attemptId,
        response_json: responseJson,
      });
    } catch (error) {
      console.error(`${config.label} auto-save failed:`, error);
    }
  },

  submitAttempt: async (type, responseJson) => {
    const config = ASSESSMENT_TYPES[type];
    const slice = get().assessments[type];
    if (!config || !slice?.attemptId)
      throw new Error(`No active ${config.label} attempt`);
    try {
      const response = await api.post(config.submitUrl(slice.id), {
        attempt_id: slice.attemptId,
        response_json: responseJson,
      });
      set((s) =>
        updateSlice(s.assessments, type, {
          completed: true,
          score: response.data.score,
          state: ASSESSMENT_ATTEMPT_STATUS.COMPLETED,
          attemptsCount: s.assessments[type].attemptsCount + 1,
        }),
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to submit ${config.label}:`, error);
      throw error;
    }
  },

  // Input:  type — assessment key
  // Output: void
  // Mutates: assessments[type] via startAttempt; re-sets savedResponses if the
  //          most recent attempt was abandoned via Save & Exit (in_progress status).
  // Note: setSavedResponses must come AFTER startAttempt because startAttempt
  //       resets savedResponses to null internally.
  startAttemptRestoringResponses: async (type, isCurrent = () => true) => {
    if (!isCurrent()) return;
    const attempts = await get().fetchAttempts(type);
    if (!isCurrent()) return;
    await get().startAttempt(type, isCurrent);
    if (!isCurrent()) return;
    const lastAttempt = attempts[attempts.length - 1];
    if (
      lastAttempt?.status === ASSESSMENT_ATTEMPT_STATUS.IN_PROGRESS &&
      lastAttempt.response_json
    ) {
      get().setSavedResponses(type, lastAttempt.response_json);
    }
  },

  fetchAttempts: async (type) => {
    const config = ASSESSMENT_TYPES[type];
    const slice = get().assessments[type];
    if (!config || !slice?.id) return [];
    try {
      const response = await api.get(config.attemptsUrl(slice.id));
      return response.data.attempts;
    } catch (error) {
      console.error(`Failed to fetch ${config.label} attempts:`, error);
      return [];
    }
  },

  setIsActive: (type, val) =>
    set((s) => updateSlice(s.assessments, type, { isActive: val })),

  setPercentAnswered: (type, value) =>
    set((s) => updateSlice(s.assessments, type, { percentAnswered: value })),

  setSavedResponses: (type, responses) => {
    const flat = responses
      ? Object.fromEntries(
          Object.entries(responses).map(([k, v]) => [
            k,
            // Enriched format: {"answer": value, "is_correct": ...} — unwrap to plain answer.
            // Must check for the key explicitly because answer can be null (unanswered question),
            // and null ?? v would fall back to the whole object, rendering [object Object].
            v !== null && typeof v === "object" && "answer" in v ? v.answer : v,
          ]),
        )
      : responses;
    set((s) => updateSlice(s.assessments, type, { savedResponses: flat }));
  },

  isAnyActive: () => ASSESSMENT_KEYS.some((k) => get().assessments[k].isActive),

  resetAll: () =>
    set({
      assessments: Object.fromEntries(
        ASSESSMENT_KEYS.map((k) => [k, makeEmptySlice()]),
      ),
    }),
});

export const useAssessmentStore = createStore("AssessmentStore", base);

export const useExitTicketStore = useAssessmentStore;

export const selectIsAssessmentActive = (state) =>
  ASSESSMENT_KEYS.some((key) => state.assessments?.[key]?.isActive);
